import React from 'react';
import { smartParagraphs, cleanContent, isRapidFireContent, parseRapidFireContent } from '../RemnantBookPDF';

/**
 * Mirrors RemnantBookPDF.js BoldOpenerParagraph: bold the first ~6 words of the
 * opening paragraph, snapping to a natural punctuation break if one falls in
 * the 5-8 word window.
 */
function boldOpenerWordCount(words) {
  const breakIdx = words.findIndex((w, i) => i >= 4 && /[,;.!?—]/.test(w));
  return Math.min(Math.max(5, breakIdx + 1), 8) || 6;
}

/**
 * Splits content into paragraphs, accumulates them up to maxChars, and renders
 * each as a block. First ~6 words of the first paragraph render bold to match
 * the printed chapter opener.
 */
function renderParagraphs(content, color, maxChars) {
  const paras = smartParagraphs(cleanContent(content || ''));
  if (!paras.length) return null;
  // Accumulate full paragraphs until we exceed maxChars. Truncate only the last
  // paragraph if needed so we never cut a paragraph that we promised to render.
  const kept = [];
  let used = 0;
  for (const p of paras) {
    if (used + p.length <= maxChars) {
      kept.push(p);
      used += p.length;
    } else {
      const remaining = maxChars - used;
      if (remaining > 20) kept.push(p.slice(0, remaining));
      break;
    }
  }
  return kept.map((para, i) => {
    if (i === 0) {
      const words = para.split(' ');
      const bold = boldOpenerWordCount(words);
      const head = words.slice(0, bold).join(' ');
      const tail = words.slice(bold).join(' ');
      return (
        <p key={i} style={{ margin: 0, marginBottom: 4, color }}>
          <span style={{ fontWeight: 700 }}>{head}</span>
          {tail ? ' ' + tail : ''}
        </p>
      );
    }
    return (
      <p key={i} style={{ margin: 0, marginBottom: 4, color }}>{para}</p>
    );
  });
}

/**
 * ChapterPagePreview — Simulated printed book page preview.
 * Reads chapter state and renders a visual approximation of the printed output.
 * Used in two contexts:
 *   1. Sticky right column on >=1200px screens
 *   2. Modal overlay on tablets/smaller screens
 *
 * Parity rule (COVER-001 spirit): paragraph splitting + bold opener match
 * RemnantBookPDF.js exactly via the shared smartParagraphs / cleanContent
 * helpers. Drop caps, photos, and captions are intentionally not mirrored
 * here — the preview shows paragraph structure and opener style only.
 */
export default function ChapterPagePreview({ title, content, layout, photoUrl, caption, photoFocusX, photoFocusY, photoOrientation }) {
  const hasContent = (title || '').trim() || (content || '').trim();
  const photoSrc = layout === 'text-only' ? null : photoUrl;
  const focusX = photoFocusX ?? 50;
  const focusY = photoFocusY ?? 50;

  // Truncate story text for preview
  const maxChars = layout === 'text-only' ? 600 : 400;
  const storyPreview = (content || '').slice(0, maxChars);

  // Common elements
  const chapterLabel = (
    <div style={{ fontSize: 6.5, fontWeight: 500, fontFamily: 'system-ui, sans-serif', textTransform: 'uppercase', letterSpacing: '0.12em', color: '#888', marginBottom: 3 }}>
      Chapter
    </div>
  );
  const chapterTitle = (
    <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 13, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 4 }}>
      {title || 'Untitled'}
    </div>
  );
  const amberRule = (
    <div style={{ width: 20, height: 1, background: '#d97706', marginBottom: 6 }} />
  );
  const captionEl = caption ? (
    <div style={{ fontSize: 7, fontStyle: 'italic', color: '#666', textAlign: 'center', marginTop: 4 }}>
      {caption}
    </div>
  ) : null;

  const storyText = (rawText, color = '#444') => {
    const fullText = rawText || '';
    return (
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 7.5, lineHeight: 1.55, color }}>
          {renderParagraphs(fullText, color, maxChars)}
        </div>
        {fullText.length >= maxChars - 10 && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 40, background: layout === 'photo-spread' ? 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.65))' : 'linear-gradient(to bottom, transparent, #fdfbf7)', pointerEvents: 'none' }} />
        )}
      </div>
    );
  };

  const photoPlaceholder = (style = {}) => (
    <div style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.06), rgba(239,235,224,0.60))', ...style }} />
  );

  // TB-104 Hot Seat Q&A: detect sentinel before layout branching.
  // Mirrors RemnantBookPDF.js RapidFirePage: italic muted question label,
  // body-weight answer beneath, flowing down one page card.
  // Prose chapters never trigger this branch (backward compatible).
  if (isRapidFireContent(content || '')) {
    const qaPairs = parseRapidFireContent(content || '');
    const cardStyle = {
      width: '100%', maxWidth: 240, aspectRatio: '6/9', background: '#fdfbf7',
      borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)',
      position: 'relative', padding: '14px 12px 20px', display: 'flex',
      flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden',
    };
    return (
      <div style={cardStyle}>
        {/* Small italic header + rule — mirrors RapidFirePage textPageHeader */}
        <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 8, fontStyle: 'italic', color: '#b0a090', marginBottom: 4 }}>
          {title || 'Rapid Fire'}
        </div>
        <div style={{ width: 20, height: 1, background: '#d4c4a8', marginBottom: 8 }} />
        {/* Q&A pairs */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {qaPairs.map((pair, i) => (
            <div key={i} style={{ marginBottom: 7 }}>
              <div style={{ fontFamily: "'Lora', Georgia, serif", fontStyle: 'italic', fontSize: 6.5, color: '#7a6e64', lineHeight: 1.4, marginBottom: 2 }}>
                {pair.question}
              </div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: 7.5, color: '#2a2520', lineHeight: 1.55 }}>
                {pair.answer}
              </div>
            </div>
          ))}
          {/* Fade-out at bottom when content overflows */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 30, background: 'linear-gradient(to bottom, transparent, #fdfbf7)', pointerEvents: 'none' }} />
        </div>
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 7.5, fontFamily: 'Georgia, serif', color: '#aaa' }}>2</div>
      </div>
    );
  }

  // Empty state
  if (!hasContent) {
    return (
      <div style={{ width: '100%', maxWidth: 240, aspectRatio: '6/9', background: '#fdfbf7', borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px 12px 20px' }}>
        <div style={{ fontSize: 9, color: 'rgba(15,23,42,0.30)', textAlign: 'center' }}>
          Your page will appear here as you write
        </div>
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 7.5, fontFamily: 'Georgia, serif', color: '#aaa' }}>2</div>
      </div>
    );
  }

  // ── PHOTO SPREAD — TWO-PAGE SPREAD (mat-framed photo page + facing text page) ──
  // Mirrors RemnantBookPDF.js PhotoSpreadPage exactly: a 630×810 page with a 48pt
  // white mat, the photo filling the inset area (object-fit cover, focus-aware),
  // a slim dark band (15% of the photo height) backing the title — and the body
  // text on the FACING page, not over the photo. The old single full-bleed card
  // forced a landscape photo into a 6/9 box and cropped the subjects out.
  if (layout === 'photo-spread') {
    const matX = (48 / 630) * 100;   // 7.62% — horizontal mat
    const matY = (48 / 810) * 100;   // 5.93% — vertical mat
    const bandH = (((810 - 96) * 0.15) / 810) * 100; // ≈13.2% of card height
    const cardBase = {
      width: '100%', maxWidth: 240, aspectRatio: '630 / 810',
      borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)',
      position: 'relative', boxSizing: 'border-box',
    };
    const pageFooter = (num, color = '#aaa') => (
      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 7.5, fontFamily: 'Georgia, serif', color }}>{num}</div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
        {/* ── MINI PAGE 1 — mat-framed photo with slim title band ── */}
        <div style={{ ...cardBase, background: '#faf8f3', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: `${matY}%`, bottom: `${matY}%`, left: `${matX}%`, right: `${matX}%`, overflow: 'hidden' }}>
            {photoSrc ? (
              <img src={photoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${focusX}% ${focusY}%` }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, rgba(15,23,42,0.06), rgba(239,235,224,0.60))' }} />
            )}
          </div>
          {/* Slim dark band — only behind the title text */}
          <div style={{ position: 'absolute', bottom: `${matY}%`, left: `${matX}%`, right: `${matX}%`, height: `${bandH}%`, background: 'rgba(0,0,0,0.40)' }} />
          <div style={{ position: 'absolute', bottom: `${matY + 2.5}%`, left: `${matX + 4}%`, right: `${matX + 4}%` }}>
            <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 6, color: 'rgba(250,248,243,0.6)', letterSpacing: '0.18em', marginBottom: 2 }}>CHAPTER</div>
            <div style={{ fontFamily: "'Lora', Georgia, serif", fontStyle: 'italic', fontSize: 12, color: '#faf8f3', lineHeight: 1.2 }}>{title || 'Untitled'}</div>
          </div>
          {pageFooter(1, 'rgba(250,248,243,0.45)')}
        </div>
        {/* ── MINI PAGE 2 — facing text page (small header + body with bold opener) ── */}
        <div style={{ ...cardBase, background: '#fdfbf7', padding: '14px 12px 20px', display: 'flex', flexDirection: 'column' }}>
          {title ? <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 8, fontStyle: 'italic', color: '#b0a090', marginBottom: 4 }}>{title}</div> : null}
          <div style={{ width: 20, height: 1, background: '#d4c4a8', marginBottom: 8 }} />
          {storyText(content || '')}
          {captionEl}
          {pageFooter(2)}
        </div>
      </div>
    );
  }

  // ── CLASSIC — TWO-PAGE SPREAD (title page + text page) ──
  // Classic in the PDF generates two pages per chapter: a centered title page
  // with the photo, chapter label, title, and divider; then a text page with
  // the small italic chapter header, divider, and body text. The preview
  // mirrors this exactly with two mini pages stacked vertically so the user
  // sees what will actually print.
  if (layout === 'classic' || !layout) {
    const isPortrait = photoOrientation === 'portrait';
    const photoWidth = isPortrait ? '50%' : '70%';
    const photoAspect = isPortrait ? '0.96 / 1' : '1.65 / 1';
    const cardBase = {
      width: '100%', maxWidth: 240, aspectRatio: '6/9', background: '#fdfbf7',
      borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)',
      position: 'relative', boxSizing: 'border-box',
    };
    const pageFooter = (num) => (
      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 7.5, fontFamily: 'Georgia, serif', color: '#aaa' }}>{num}</div>
    );
    // Small italic chapter header for the text page (matches PDF s.textPageHeader)
    const textPageHeader = title ? (
      <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 8, fontStyle: 'italic', color: '#b0a090', marginBottom: 4 }}>
        {title}
      </div>
    ) : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
        {/* ── MINI PAGE 1 — Title page (photo + chapter + title centered) ── */}
        <div style={{ ...cardBase, padding: '14px 12px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
          {photoSrc ? (
            <div style={{ width: photoWidth, aspectRatio: photoAspect, overflow: 'hidden', borderRadius: 3, marginBottom: 10 }}>
              <img src={photoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${focusX}% ${focusY}%` }} />
            </div>
          ) : photoPlaceholder({ width: photoWidth, aspectRatio: photoAspect, borderRadius: 3, marginBottom: 10 })}
          {chapterLabel}
          <div style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 13, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 4, textAlign: 'center', padding: '0 8px' }}>
            {title || 'Untitled'}
          </div>
          {amberRule}
          {captionEl}
          {pageFooter(1)}
        </div>
        {/* ── MINI PAGE 2 — Text page (small header + body with bold opener) ── */}
        <div style={{ ...cardBase, padding: '14px 12px 20px', display: 'flex', flexDirection: 'column' }}>
          {textPageHeader}
          <div style={{ width: 20, height: 1, background: '#d4c4a8', marginBottom: 8 }} />
          {storyText(content || '')}
          {pageFooter(2)}
        </div>
      </div>
    );
  }

  // ── PHOTO HEADER — narrow strip photo, text fills rest ──
  if (layout === 'story-page') {
    return (
      <div style={{ width: '100%', maxWidth: 240, aspectRatio: '6/9', background: '#fdfbf7', borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)', position: 'relative', padding: '0 0 28px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>
        {/* Photo strip */}
        {photoSrc ? (
          <div style={{ width: '100%', aspectRatio: '1.75 / 1', overflow: 'hidden', flexShrink: 0 }}>
            <img src={photoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${focusX}% ${focusY}%` }} />
          </div>
        ) : photoPlaceholder({ width: '100%', aspectRatio: '1.75 / 1', flexShrink: 0 })}
        <div style={{ padding: '12px 20px 0', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {captionEl && <div style={{ marginBottom: 8 }}>{captionEl}</div>}
          {chapterLabel}
          {chapterTitle}
          {amberRule}
          {storyText(content || '')}
        </div>
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 7.5, fontFamily: 'Georgia, serif', color: '#aaa' }}>2</div>
      </div>
    );
  }

  // ── TEXT ONLY — no photo ──
  if (layout === 'text-only') {
    return (
      <div style={{ width: '100%', maxWidth: 240, aspectRatio: '6/9', background: '#fdfbf7', borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)', position: 'relative', padding: '14px 12px 20px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        {chapterLabel}
        {chapterTitle}
        {amberRule}
        {storyText(content || '')}
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 7.5, fontFamily: 'Georgia, serif', color: '#aaa' }}>2</div>
      </div>
    );
  }

  return null;
}
